import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppConfig } from '@app/app.config';
import { ApiResponse } from '@app/models/_base/apiResponse.model';
import { NavCourses } from '@app/models/courses/navCourses.model';
import {
  CourseComparisonDetails,
  FinalisedShortlist,
  RateCourseResponse,
  ResearchAreasResponse,
  ResearchAreaSummary,
  ResearchSummary,
  ShortlistResponse,
  UpdateResearchAreaLink
} from '@app/models/research/research.models';
import { Observable } from 'rxjs';

import { CurrentQuestionnaireService } from './current-questionnaire.service';

@Injectable()
export class ResearchService {
  private researchAreas;
  private observResearchAreas: Observable<any>;

  private shortListCache;
  private shortListObservable: Observable<any>;

  public navCourses: NavCourses = {
    pre: { id: null, courseName: null },
    nex: { id: null, courseName: null }
  };

  constructor(
    private http: HttpClient,
    private currentQuestionnaire: CurrentQuestionnaireService
  ) {}

  addCourseToShortlist(id: string | number): Observable<ApiResponse> {
    const url =
      AppConfig.ApiUrl + `research/${this.currentQuestionnaire.get()}/shortlist/course/${id}`;
    return this.http.post<ApiResponse>(url, null);
  }

  removeCourseFromShortlist(id: string | number): Observable<ApiResponse> {
    const url =
      AppConfig.ApiUrl + `research/${this.currentQuestionnaire.get()}/shortlist/course/${id}`;
    return this.http.delete<ApiResponse>(url);
  }

  getShortlist(): Observable<ShortlistResponse> {
    if (this.shortListObservable) {
      return this.shortListObservable;
    } else {
      const url = AppConfig.ApiUrl + `research/${this.currentQuestionnaire.get()}/shortlist`;
      this.shortListObservable = this.http
        .get<ShortlistResponse>(url)
        .map(data => {
          this.shortListObservable = null;
          this.shortListCache = data as ShortlistResponse;
          return this.shortListCache;
        })
        .share();
      return this.shortListObservable;
    }
  }

  getNavCourses(courseId: string) {
    // clear navigation for upcoming course
    this.navCourses.pre.id = null;
    this.navCourses.nex.id = null;

    if (this.shortListCache != null) {
      const courseList = this.shortListCache.courses;

      // Create the navigations prev and next links
      for (let key = 0; key < courseList.length; key++) {
        const preCounter = key - 1;
        const nexCounter = key + 1;
        if (courseId == courseList[key].courseId) {
          if (key > 0) {
            // now we have previous
            this.navCourses.pre.id = courseList[preCounter].courseId;
            this.navCourses.pre.courseName = courseList[preCounter].title;
          } else {
            // Loop to last shortlist
            this.navCourses.pre.id = courseList[courseList.length - 1].courseId;
            this.navCourses.pre.courseName = courseList[courseList.length - 1].title;
          }

          if (key < courseList.length - 1) {
            // now we have next
            this.navCourses.nex.id = courseList[nexCounter].courseId;
            this.navCourses.nex.courseName = courseList[nexCounter].title;
          } else {
            // Loop to first shortlist
            this.navCourses.nex.id = courseList[0].courseId;
            this.navCourses.nex.courseName = courseList[0].title;
          }

          return this.navCourses;
        }
      }
    }

    return this.navCourses;
  }

  getShortlistComparison(courseIds: string[] = []): Observable<CourseComparisonDetails[]> {
    const url =
      AppConfig.ApiUrl + `research/${this.currentQuestionnaire.get()}/shortlist/comparison`;
    return this.http.get<CourseComparisonDetails[]>(url).catch(err => Observable.throw(err.error));
  }

  rateCourse(courseId: string, label: string, rating: number): Observable<RateCourseResponse> {
    if (rating < -4 || rating > 4) {
      throw Error('Rating must be between 1 and 5');
    }

    const url =
      AppConfig.ApiUrl + `research/${this.currentQuestionnaire.get()}/rating/course/${courseId}`;
    const model = { label: label, rating: rating };
    return this.http.post<RateCourseResponse>(url, model).catch(err => Observable.throw(err.error));
  }

  deleteCourseRating(ratingId: string): Observable<ApiResponse> {
    const url =
      AppConfig.ApiUrl + `research/${this.currentQuestionnaire.get()}/rating/course/${ratingId}`;
    return this.http.delete<ApiResponse>(url).catch(err => Observable.throw(err.error));
  }

  updateInstitutionNote(institutionId: string, note: string): Observable<ApiResponse> {
    const url =
      AppConfig.ApiUrl +
      `research/${this.currentQuestionnaire.get()}/note/institution/${institutionId}`;
    const model = { text: note };
    return this.http.put<ApiResponse>(url, model).catch(err => Observable.throw(err.error));
  }

  deleteInstitutionNote(institutionId: string): Observable<ApiResponse> {
    const url =
      AppConfig.ApiUrl +
      `research/${this.currentQuestionnaire.get()}/note/institution/${institutionId}`;
    return this.http.delete<ApiResponse>(url).catch(err => Observable.throw(err.error));
  }

  updateCourseNote(courseId: string, note: string): Observable<ApiResponse> {
    const url =
      AppConfig.ApiUrl + `research/${this.currentQuestionnaire.get()}/note/course/${courseId}`;
    const model = { text: note };
    return this.http.put<ApiResponse>(url, model).catch(err => Observable.throw(err.error));
  }

  deleteCourseNote(courseId: string): Observable<ApiResponse> {
    const url =
      AppConfig.ApiUrl + `research/${this.currentQuestionnaire.get()}/note/course/${courseId}`;
    return this.http.delete<ApiResponse>(url).catch(err => Observable.throw(err.error));
  }

  getResearchAreas(): Observable<ResearchAreasResponse> {
    const url = AppConfig.ApiUrl + `research/${this.currentQuestionnaire.get()}/area`;
    return this.http.get<ResearchAreasResponse>(url).catch(err => Observable.throw(err.error));
  }

  getDefaultResearchAreas(): Observable<ResearchAreaSummary[]> {
    if (this.researchAreas) {
      return Observable.of(this.researchAreas);
    } else if (this.observResearchAreas) {
      return this.observResearchAreas;
    } else {
      const url = AppConfig.ApiUrl + 'data/researchareas';
      this.observResearchAreas = this.http
        .get<ResearchAreaSummary[]>(url)
        .map(data => {
          this.observResearchAreas = null;
          this.researchAreas = data;
          return this.researchAreas;
        })
        .share();
      return this.observResearchAreas;
    }
  }

  addOrUpdateResearchArea(model: ResearchAreaSummary): Observable<ResearchAreaSummary> {
    const url = AppConfig.ApiUrl + `research/${this.currentQuestionnaire.get()}/area`;
    if (model.id) {
      return this.http
        .put<ResearchAreaSummary>(url, model)
        .catch(err => Observable.throw(err.error));
    }
    return this.http
      .post<ResearchAreaSummary>(url, model)
      .catch(err => Observable.throw(err.error));
  }

  deleteResearchArea(id: number): Observable<ApiResponse> {
    const url = AppConfig.ApiUrl + `research/${this.currentQuestionnaire.get()}/area/${id}`;
    return this.http.delete<ApiResponse>(url).catch(err => Observable.throw(err.error));
  }

  addOrUpdateInstitutionLink(model: UpdateResearchAreaLink): Observable<ApiResponse> {
    const url =
      AppConfig.ApiUrl +
      `research/${this.currentQuestionnaire.get()}/area/${model.aid}/institution/${model.linkId}`;
    return this.http.post<ApiResponse>(url, model).catch(err => Observable.throw(err.error));
  }

  deleteInstitutionLink(model: UpdateResearchAreaLink): Observable<ApiResponse> {
    const url =
      AppConfig.ApiUrl +
      `research/${this.currentQuestionnaire.get()}/area/${model.aid}/institution/${model.linkId}`;
    return this.http.delete<ApiResponse>(url).catch(err => Observable.throw(err.error));
  }

  addOrUpdateCourseLink(model: UpdateResearchAreaLink): Observable<ApiResponse> {
    const url =
      AppConfig.ApiUrl +
      `research/${this.currentQuestionnaire.get()}/area/${model.aid}/course/${model.linkId}`;
    return this.http.post<ApiResponse>(url, model).catch(err => Observable.throw(err.error));
  }

  deleteCourseLink(model: UpdateResearchAreaLink): Observable<ApiResponse> {
    const url =
      AppConfig.ApiUrl +
      `research/${this.currentQuestionnaire.get()}/area/${model.aid}/course/${model.linkId}`;
    return this.http.delete<ApiResponse>(url).catch(err => Observable.throw(err.error));
  }

  getFinalisedList(): Observable<FinalisedShortlist> {
    const url = AppConfig.ApiUrl + `research/${this.currentQuestionnaire.get()}/finalisedlist`;
    return this.http.get<FinalisedShortlist>(url).catch(err => Observable.throw(err.error));
  }

  exportShortlist(emails: string): Observable<ApiResponse> {
    const url =
      AppConfig.ApiUrl + `research/${this.currentQuestionnaire.get()}/finalisedlist/export`;
    const model = { email: emails };
    return this.http.post<ApiResponse>(url, model).catch(err => Observable.throw(err.error));
  }

  getResearchSummary(): Observable<ResearchSummary> {
    const url = AppConfig.ApiUrl + `research/${this.currentQuestionnaire.get()}/summary`;
    return this.http.get<ResearchSummary>(url).catch(err => Observable.throw(err.error));
  }

  updateShortlistOrder(shortlistOrder) {
    const url = AppConfig.ApiUrl + `research/${this.currentQuestionnaire.get()}/shortlist/order`;
    return this.http.post(url, shortlistOrder).catch(err => Observable.throw(err.error));
  }
}



// WEBPACK FOOTER //
// ./src/app/services/research.service.ts